import { jwtDecode } from 'jwt-decode';
import { setExpiry, setExpiryDate, setDaysRemaining, logout, setPlanName, setHasTrialed } from 'config/redux/slices/userSlice';
import api from 'config/api.config';
import axios from 'axios';
import { Dispatch } from 'redux';
import { logger } from 'utils/logger';

// Create a separate axios instance for refresh token calls (without interceptors)
const refreshApi = axios.create({
  baseURL: process.env.REACT_APP_FAMOUS_CLICKER_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Define interface for JWT payload
interface JwtPayload {
  exp: number;
  role?: string;
  fullName?: string;
  _id?: string;
}

// Define interface for refresh token response
interface RefreshTokenResponse {
  accessToken: string;
}

let accessToken: string | null = null;

export const setAccessToken = (token: string): void => {
  accessToken = token;
  sessionStorage.setItem('token', token);
};

export const getAccessToken = (): string | null => {
  const token = accessToken || sessionStorage.getItem('token');
  return token;
};

export const clearAccessToken = (): void => {
  accessToken = null;
  sessionStorage.removeItem('token');
};

export const getRefreshToken = (): string | null => {
  try {
    // First try to get from cookie
    const cookieMatch = document.cookie
      .split('; ')
      .find((row) => row.startsWith('refreshToken='));

    if (cookieMatch) {
      const refreshToken = cookieMatch.split('=').slice(1).join('='); // Handle tokens with '=' in them
      if (refreshToken) return refreshToken;
    }

    // Cookie is the only storage for refresh tokens (no localStorage fallback)
    return null;
  } catch {
    return null;
  }
};

export const clearRefreshToken = (): void => {
  document.cookie = 'refreshToken=; path=/; max-age=0';
};

export const isTokenValid = (token: string | null = getAccessToken(), dispatch: Dispatch | null = null): boolean => {
  if (!token) {
    return false;
  }

  try {
    const decoded: JwtPayload = jwtDecode(token);
    const currentTime: number = Date.now() / 1000;

    if (decoded.exp < currentTime) {
      // Don't logout here - let the refresh mechanism handle it
      return false;
    }
    return true;
  } catch {
    if (dispatch) {
      try {
        dispatch(logout());
      } catch (dispatchError) {
        logger.warn('Redux dispatch failed in auth flow:', dispatchError);
      }
    }
    clearAccessToken();
    return false;
  }
};

export const validateTokenWithServer = async (token: string, dispatch: Dispatch | null = null): Promise<boolean> => {
  try {
    const response = await api.get('/api/auth/validate-auth', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Handle response.data as ServerValidationResponse or direct object
    const validationData = response.data.data || response.data;
    if (!validationData || typeof validationData.isExpired === 'undefined') {
      throw new Error('Invalid server validation response');
    }

    if (dispatch) {
      try {
        dispatch(setExpiry(validationData.isExpired));
        dispatch(setExpiryDate(validationData.expiryDate || ''));
        dispatch(setDaysRemaining(validationData.daysRemaining || 0));
        dispatch(setPlanName(validationData.planName || ''));
        dispatch(setHasTrialed(validationData.hasTrialed === true));
      } catch (dispatchError) {
        logger.warn('Redux dispatch failed in auth flow:', dispatchError);
      }
    }

    return true;
  } catch {
    // Don't logout here - the API interceptor handles 401 and will try refresh
    return false;
  }
};

export const checkFullTokenValidation = async (token: string, dispatch: Dispatch): Promise<boolean> => {
  if (localStorage.getItem('justLoggedIn') === 'true') {
    return true;
  }

  // Check if token is valid locally
  if (!isTokenValid(token, dispatch)) {
    // Token is expired, try to refresh it
    const newToken = await refreshAccessToken(dispatch);
    if (newToken) {
      // Validate the new token with server
      return await validateTokenWithServer(newToken, dispatch);
    }
    return false;
  }

  return await validateTokenWithServer(token, dispatch);
};

export const getUserRole = (): string | null => {
  let token: string | null = null;
  try {
    token = getAccessToken();
  } catch (error) {
    return null;
  }

  if (token) {
    try {
      const decoded: JwtPayload = jwtDecode(token);
      return decoded.role || null;
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const getUserName = (): string | null => {
  let token: string | null = null;
  try {
    token = getAccessToken();
  } catch (error) {
    return null;
  }

  if (token) {
    try {
      const decoded: JwtPayload = jwtDecode(token);
      return decoded.fullName || null;
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const getUserId = (): string | null => {
  let token: string | null = null;
  try {
    token = getAccessToken();
  } catch (error) {
    return null;
  }

  if (token) {
    try {
      const decoded: JwtPayload = jwtDecode(token);
      return decoded._id || null;
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const refreshAccessToken = async (dispatch: Dispatch | null = null): Promise<string | null> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  try {
    // Use refreshApi (without interceptors) to avoid sending expired token in header
    const response = await refreshApi.post<RefreshTokenResponse>('/api/auth/refresh-token', { refreshToken });
    const newAccessToken: string = response.data.accessToken;

    if (!newAccessToken) {
      throw new Error('No access token received from refresh endpoint');
    }

    // Update the token in memory and sessionStorage
    setAccessToken(newAccessToken);

    return newAccessToken;
  } catch (error: unknown) {
    // Only logout if it's an auth error (401, 403) not a network error
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 403) {
      if (dispatch) {
        try {
          dispatch(logout());
        } catch {
          // Silently handle dispatch error
        }
      }
      clearAccessToken();
      clearRefreshToken();
    }
    return null;
  }
};

export const getTokenExpirationTime = (token: string): number | null => {
  try {
    const decoded: JwtPayload = jwtDecode(token);
    return decoded.exp * 1000; // Convert to milliseconds
  } catch (error) {
    return null;
  }
};

export const performFullLogout = (dispatch: Dispatch): void => {
  clearAccessToken();
  clearRefreshToken();
  localStorage.removeItem('justLoggedIn');
  try {
    dispatch(logout());
  } catch {
    // Silently handle dispatch error
  }
};